/**
 * Announcements hooks (Phase X.5.1) — Olive hub banner.
 *
 * Thin TanStack Query layer over `announcementsApi` (src/lib/api.ts),
 * mirroring the aiDrafts.ts hook convention. The list query is gated by
 * the caller (`enabled: canViewStaffTools(whoami)`) so a user without hub
 * access never fires a request that would 403. Create / delete mutations
 * invalidate the list so the banner reflects the change immediately.
 *
 * Server is authoritative on both reads (active-list computed server-side)
 * and writes (manage_announcements re-derived from ctx.role); these hooks
 * carry no authorization logic of their own.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  announcementsApi,
  type AnnouncementWire,
  type AnnouncementCreateInput,
} from '@/lib/api';

const ANNOUNCEMENTS_STALE_MS = 60_000;

export function announcementsQueryKey(slug: string): readonly unknown[] {
  return ['announcements', slug];
}

export interface UseAnnouncementsResult {
  announcements: readonly AnnouncementWire[];
  /** X.5.2 — active announcements the caller has not yet seen (0 if unknown). */
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
}

/**
 * List the tenant's active announcements (+ the caller's unread count). Pass
 * `enabled: false` (e.g. when the user lacks view_staff_tools) to keep the
 * hook in stable position above a capability gate without firing the fetch.
 *
 * Shared query key: the hub banner AND the AppShell notification bell both
 * call this, so they hit one cache entry (no second poll).
 */
export function useAnnouncements(
  slug: string,
  opts: { enabled?: boolean } = {},
): UseAnnouncementsResult {
  const query = useQuery({
    queryKey: announcementsQueryKey(slug),
    queryFn: () => announcementsApi.list(slug),
    staleTime: ANNOUNCEMENTS_STALE_MS,
    enabled: opts.enabled ?? true,
  });
  return {
    announcements: query.data?.announcements ?? [],
    unreadCount: query.data?.unread_count ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Mark the caller as having seen announcements (clears unread). Invalidates
 * the list so unread_count refetches to 0.
 */
export function useMarkAnnouncementsSeen(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => announcementsApi.markSeen(slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementsQueryKey(slug) });
    },
  });
}

/** Create an announcement; invalidates the list on success. */
export function useCreateAnnouncement(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AnnouncementCreateInput) => announcementsApi.create(slug, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementsQueryKey(slug) });
    },
  });
}

/** Hard-delete an announcement; invalidates the list on success. */
export function useDeleteAnnouncement(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => announcementsApi.remove(slug, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementsQueryKey(slug) });
    },
  });
}
